import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getSupabase } from "@/integrations/supabase/lazy";

const SESSION_KEY = "parts_cart_session_v1";

export interface PartsCartItem {
  id: string;
  cart_id: string;
  product_id: string;
  sku: string | null;
  title_snapshot: string;
  image_url: string | null;
  unit_price: number;
  qty: number;
  line_total: number;
}

export interface PartsCart {
  id: string;
  customer_id: string | null;
  session_token: string | null;
  currency: string;
  subtotal: number;
  discount_total: number;
  shipping_total: number;
  tax_total: number;
  grand_total: number;
}

interface Ctx {
  cart: PartsCart | null;
  items: PartsCartItem[];
  itemCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  setItem: (productId: string, qty: number) => Promise<void>;
  addItem: (productId: string, qty?: number) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  clear: () => Promise<void>;
  mergeOnLogin: () => Promise<void>;
  /** Load a staff quote into the cart at its quoted prices. */
  applyQuote: (token: string) => Promise<void>;
}

const PartsCartContext = createContext<Ctx | null>(null);

async function callCart(action: string, body: Record<string, unknown> = {}) {
  const session = localStorage.getItem(SESSION_KEY) || "";
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const client = await getSupabase();
  const { data, error } = await client.functions.invoke("parts-cart", {
    // Keep the cart session in the JSON body instead of a custom browser
    // header. Custom headers force an extra CORS preflight and some browsers /
    // proxies surface that failure only as "Failed to send a request to the
    // Edge Function" before the function code ever runs.
    body: { action, session_token: session || undefined, ...body },
    headers,
  });
  if (error) throw error;
  if (data?.session_token) {
    localStorage.setItem(SESSION_KEY, data.session_token);
  }
  const stock = (data as any)?.stock as
    | { available: number; adjusted: boolean; out_of_stock: boolean; product_id: string }
    | null
    | undefined;
  if (stock?.out_of_stock) {
    const { toast } = await import("sonner");
    toast.error("Out of stock", { description: "This item is currently unavailable." });
  } else if (stock?.adjusted) {
    const { toast } = await import("sonner");
    toast.warning(`Only ${stock.available} in stock`, {
      description: "Quantity was adjusted to match available stock.",
    });
  }
  return data as { cart: PartsCart | null; items: PartsCartItem[] };
}

export function PartsCartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<PartsCart | null>(null);
  const [items, setItems] = useState<PartsCartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const mergedRef = useRef(false);

  const apply = (d: { cart: PartsCart | null; items: PartsCartItem[] } | undefined) => {
    if (!d) return;
    setCart(d.cart || null);
    setItems(d.items || []);
  };

  const refresh = useCallback(async () => {
    try {
      const d = await callCart("get");
      apply(d);
    } catch (e) {
      console.warn("[partsCart] refresh failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const setItem = useCallback(async (productId: string, qty: number) => {
    apply(await callCart("set_item", { product_id: productId, qty }));
  }, []);
  const addItem = useCallback(async (productId: string, qty = 1) => {
    apply(await callCart("add_item", { product_id: productId, qty }));
  }, []);
  const removeItem = useCallback(async (productId: string) => {
    apply(await callCart("remove_item", { product_id: productId }));
  }, []);
  const clear = useCallback(async () => {
    apply(await callCart("clear"));
  }, []);
  const mergeOnLogin = useCallback(async () => {
    apply(await callCart("merge"));
  }, []);
  const applyQuote = useCallback(async (token: string) => {
    apply(await callCart("apply_quote", { quote_token: token }));
  }, []);

  useEffect(() => {
    if (!window.location.pathname.startsWith("/parts-catalog")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let started = false;
    let unsubscribe: (() => void) | undefined;
    const start = () => {
      if (cancelled || started) return;
      started = true;
      events.forEach((event) => window.removeEventListener(event, start));
      refresh();
      getSupabase().then((client) => {
        if (cancelled) return;
        const { data: { subscription } } = client.auth.onAuthStateChange(async (event) => {
          if (event === "SIGNED_IN" && !mergedRef.current) {
            mergedRef.current = true;
            try {
              apply(await callCart("merge"));
            } catch {
              await refresh();
            }
          } else if (event === "SIGNED_OUT") {
            mergedRef.current = false;
            await refresh();
          }
        });
        unsubscribe = () => subscription.unsubscribe();
      });
    };
    const events: (keyof WindowEventMap)[] = ["wheel", "pointerdown", "touchstart", "keydown"];
    events.forEach((event) => window.addEventListener(event, start, { passive: true, once: true }));
    return () => {
      cancelled = true;
      events.forEach((event) => window.removeEventListener(event, start));
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemCount = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  const value: Ctx = { cart, items, itemCount, loading, refresh, setItem, addItem, removeItem, clear, mergeOnLogin, applyQuote };
  return <PartsCartContext.Provider value={value}>{children}</PartsCartContext.Provider>;
}

/**
 * Standalone cart access for pages rendered outside PartsCartProvider
 * (e.g. /rv-search showing parts results). Talks to the cart function
 * directly and keeps its own local snapshot instead of throwing.
 */
function useStandalonePartsCart(): Ctx {
  const [cart, setCart] = useState<PartsCart | null>(null);
  const [items, setItems] = useState<PartsCartItem[]>([]);
  const [loading, setLoading] = useState(false);

  const apply = (d: { cart: PartsCart | null; items: PartsCartItem[] } | undefined) => {
    if (!d) return;
    setCart(d.cart || null);
    setItems(d.items || []);
  };

  const run = useCallback(async (action: string, body?: Record<string, unknown>) => {
    setLoading(true);
    try {
      apply(await callCart(action, body));
    } finally {
      setLoading(false);
    }
  }, []);

  const itemCount = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return useMemo<Ctx>(
    () => ({
      cart,
      items,
      itemCount,
      loading,
      refresh: () => run("get"),
      setItem: (product_id, qty) => run("set_item", { product_id, qty }),
      addItem: (product_id, qty = 1) => run("add_item", { product_id, qty }),
      removeItem: (product_id) => run("remove_item", { product_id }),
      clear: () => run("clear"),
      mergeOnLogin: () => run("merge"),
      applyQuote: (token: string) => run("apply_quote", { quote_token: token }),
    }),
    [cart, items, itemCount, loading, run],
  );
}

export function usePartsCart(): Ctx {
  const ctx = useContext(PartsCartContext);
  const fallback = useStandalonePartsCart();
  return ctx ?? fallback;
}

export function getPartsCartSession(): string | null {
  return localStorage.getItem(SESSION_KEY);
}